import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../../services/server";

/**
 * Seller- / buyer-side workspace settings page.
 *
 * Shared by /seller/<orgnr>/team and /buyer/<orgnr>/team — counterparties
 * never get the full broker WorkspaceSettings page (which exposes invite
 * forms, NDA editor, and the danger zone). Instead they get a focused page
 * for managing the people from their own company who can see this deal.
 *
 * Backend endpoints already exist:
 *   GET  /users/workspaces/<key>/users/                 — list workspace users
 *   POST /users/workspaces/<key>/users/                 — add by user_id+company_id
 *   DELETE /users/workspaces/<key>/users/<user_id>/     — remove (soft-blocked at backend)
 *   GET  /users/companies/me/members/?company_id=<id>   — list tenant members
 *
 * Permission notes:
 *   - The seller/buyer admin's company_id is whichever company they
 *     participate in this workspace as. We resolve it via `info` from the
 *     parent layout's outlet context (seller-portal or buyer-portal), then
 *     by matching the user's company_id in /users/workspaces/<key>/users/.
 *   - Backend's _can_manage_company_users already gates: only ROLE_ADMIN
 *     of the same seller/buyer company can add/remove members.
 */
async function jsonFetch(path, opts = {}) {
  const headers = { Accept: "application/json", ...(opts.headers || {}) };
  if (opts.body && !(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  const csrf = getCsrfToken();
  if (csrf && !["GET", "HEAD"].includes((opts.method || "GET").toUpperCase())) {
    headers["X-CSRFToken"] = csrf;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers,
    body: opts.body && !(opts.body instanceof FormData) ? JSON.stringify(opts.body) : opts.body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export default function WorkspaceTeamPage() {
  const ctx = useOutletContext() || {};
  const workspaceKey = ctx.workspaceKey;

  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [mySide, setMySide] = useState("");
  const [myUserId, setMyUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState({});
  const [addRole, setAddRole] = useState({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteBusy, setInviteBusy] = useState(false);

  const reload = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      // 1) Workspace users — pick out the caller's own company by matching
      //    against /users/auth/me/ memberships.
      const me = await jsonFetch(`/users/auth/me/`);
      setMyUserId(me?.user?.id ?? me?.id ?? null);
      const memberships = me?.memberships || [];
      const myCompanies = new Set(memberships.map((m) => m.company?.id).filter(Boolean));
      setIsAdmin(memberships.some((m) => (m.role || "").toLowerCase() === "admin"));

      const wsUsers = await jsonFetch(`/users/workspaces/${workspaceKey}/users/`);
      const myWorkspaceRow = wsUsers.find((u) => myCompanies.has(u.company_id));
      const resolvedCompanyId = myWorkspaceRow?.company_id || null;
      setCompanyId(resolvedCompanyId);
      setMySide((myWorkspaceRow?.side || "").toLowerCase());

      const sameCompanyUsers = wsUsers.filter(
        (u) => u.status === "active" && u.company_id === resolvedCompanyId,
      );
      setWorkspaceUsers(sameCompanyUsers);

      // 2) Colleagues — all members of the caller's company. The
      //    IsCompanyAdmin permission honours ?company_id=X so we get the
      //    right tenant when the caller is admin of multiple.
      if (resolvedCompanyId) {
        try {
          const cols = await jsonFetch(`/users/companies/me/members/?company_id=${resolvedCompanyId}`);
          setColleagues(Array.isArray(cols) ? cols : []);
        } catch {
          setColleagues([]);
        }
      } else {
        setColleagues([]);
      }
    } catch (e) {
      setError(e?.message || "Kunde inte ladda team.");
    } finally {
      setLoading(false);
    }
  }, [workspaceKey]);

  useEffect(() => { if (workspaceKey) reload(); }, [reload, workspaceKey]);

  async function addColleague(userId) {
    if (!companyId) return;
    const role = addRole[userId] || "admin";
    setBusy((b) => ({ ...b, [userId]: true }));
    setMessage("");
    setError("");
    try {
      await jsonFetch(`/users/workspaces/${workspaceKey}/users/`, {
        method: "POST",
        body: { user_id: userId, company_id: companyId, role },
      });
      setMessage("Tillagd.");
      await reload();
    } catch (e) {
      setError(e?.message || "Kunde inte lägga till.");
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function removeMember(userId) {
    setBusy((b) => ({ ...b, [userId]: true }));
    setMessage("");
    setError("");
    try {
      await jsonFetch(`/users/workspaces/${workspaceKey}/users/${userId}/`, { method: "DELETE" });
      setMessage("Borttagen.");
      await reload();
    } catch (e) {
      setError(e?.message || "Kunde inte ta bort.");
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function inviteColleague(e) {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviteBusy(true);
    setMessage("");
    setError("");
    try {
      await jsonFetch(`/users/invitations/`, {
        method: "POST",
        body: { email, role: inviteRole, company_id: companyId },
      });
      setMessage("Inbjudan skickad.");
      setInviteEmail("");
      await reload();
    } catch (e2) {
      const msg = e2?.message || "";
      // Backend returns "That person is already a member." when the email is
      // already a tenant member. They should already appear under
      // "Tillgängliga kollegor" — surface that explicitly so the admin knows
      // what to do next.
      if (/already a member|redan|already exists/i.test(msg)) {
        setMessage(
          "Den här personen är redan medlem i ditt bolag. Hitta dem i listan ovan och tryck \"Lägg till\" för att koppla till workspacet."
        );
        setInviteEmail("");
        await reload();
      } else {
        setError(msg || "Kunde inte skicka inbjudan.");
      }
    } finally {
      setInviteBusy(false);
    }
  }

  async function changeRole(userId, newRole) {
    setBusy((b) => ({ ...b, [userId]: true }));
    setMessage("");
    setError("");
    try {
      await jsonFetch(`/users/workspaces/${workspaceKey}/users/${userId}/`, {
        method: "PATCH",
        body: { role: newRole },
      });
      setMessage("Roll uppdaterad.");
      await reload();
    } catch (e) {
      setError(e?.message || "Kunde inte uppdatera roll.");
    } finally {
      setBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  const inWorkspaceIds = useMemo(
    () => new Set(workspaceUsers.map((u) => u.user_id)),
    [workspaceUsers],
  );
  const available = colleagues.filter(
    (c) => (c.is_active !== false) && !inWorkspaceIds.has(c.user_id) && c.user_id !== myUserId,
  );

  return (
    <section className="sp-module" style={{ padding: 24 }}>
      <h2 className="sp-module-title" style={{ marginBottom: 4 }}>Team i detta workspace</h2>
      <p className="sp-module-desc" style={{ marginBottom: 20 }}>
        Lägg till eller ta bort kollegor från ditt bolag i detta projekt.
        Endast administratörer kan ändra teamet.
      </p>

      {loading && <p style={{ color: "#9ca3af" }}>Laddar…</p>}
      {message && <p className="sp-success">{message}</p>}
      {error && <p className="sp-error">{error}</p>}

      <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 8, marginTop: 16 }}>
        I workspacet
      </div>
      {workspaceUsers.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 16 }}>Inga personer tillagda ännu.</p>
      ) : (
        workspaceUsers.map((u) => (
          <div
            key={u.user_id}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{u.email || u.username}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{u.status}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {isAdmin ? (
                <>
                  <select
                    value={u.role}
                    disabled={!!busy[u.user_id]}
                    onChange={(e) => changeRole(u.user_id, e.target.value)}
                    style={{ fontSize: 13, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6 }}
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                  <button
                    onClick={() => removeMember(u.user_id)}
                    disabled={!!busy[u.user_id]}
                    style={{ padding: "4px 12px", fontSize: 12, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#dc2626", cursor: "pointer" }}
                  >
                    {busy[u.user_id] ? "…" : "Ta bort"}
                  </button>
                </>
              ) : (
                <span style={{ fontSize: 12, color: "#6b7280", textTransform: "capitalize" }}>{u.role}</span>
              )}
            </div>
          </div>
        ))
      )}

      {isAdmin && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 8, marginTop: 24 }}>
            Tillgängliga kollegor
          </div>
          {available.length === 0 ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>
              Alla aktiva kollegor i ditt bolag är redan med i workspacet. Bjud in
              en ny kollega nedan så får hen ett mejl och kan sedan läggas till här.
            </p>
          ) : (
            available.map((c) => (
              <div
                key={c.user_id}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.email || c.username}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Bolagsroll: {c.role}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={addRole[c.user_id] || "admin"}
                    disabled={!!busy[c.user_id]}
                    onChange={(e) => setAddRole((m) => ({ ...m, [c.user_id]: e.target.value }))}
                    style={{ fontSize: 13, padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 6 }}
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                  </select>
                  <button
                    onClick={() => addColleague(c.user_id)}
                    disabled={!!busy[c.user_id]}
                    style={{ padding: "4px 12px", fontSize: 12, border: "none", borderRadius: 6, background: "#111827", color: "#fff", cursor: "pointer" }}
                  >
                    {busy[c.user_id] ? "Lägger till…" : "Lägg till"}
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {isAdmin && mySide === "seller" && (
        <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 24 }}>
          Endast mäklaren kan bjuda in nya säljarsidan-kollegor till detta
          workspace. Be mäklaren skicka inbjudan så dyker personen upp under
          "Tillgängliga kollegor" här efter att de accepterat.
        </p>
      )}

      {isAdmin && mySide !== "seller" && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 8, marginTop: 24 }}>
            Bjud in ny kollega
          </div>
          <form
            onSubmit={inviteColleague}
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
          >
            <input
              type="email"
              required
              placeholder="kollega@bolaget.se"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={inviteBusy}
              style={{ flex: "1 1 220px", minWidth: 220, padding: "6px 10px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6 }}
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              disabled={inviteBusy}
              style={{ fontSize: 13, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 6 }}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={inviteBusy || !inviteEmail.trim()}
              style={{ padding: "6px 14px", fontSize: 13, border: "none", borderRadius: 6, background: "#111827", color: "#fff", cursor: "pointer" }}
            >
              {inviteBusy ? "Skickar…" : "Skicka inbjudan"}
            </button>
          </form>
          <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 6 }}>
            Mejl skickas till adressen. När personen accepterat dyker hen upp under
            "Tillgängliga kollegor" och kan läggas till i detta workspace.
          </p>
        </>
      )}
    </section>
  );
}
